import loadable, { LoadableComponent } from '@loadable/component';
import type { ComponentProps } from 'react';
import React from 'react';

// Extracts the original props type from React components without the implicit children prop.
// React.FC<Props> automatically adds `children?: ReactNode` to the props type, but we want
// to preserve the exact props interface as defined by the component author.
// This ensures LoadableComponent<Props> matches the original component's props signature.
// - First checks React.FC<P> and extracts P (the original props without children)
// - Then checks React.ComponentType<P> and extracts P (covers other component patterns)
// - Falls back to ComponentProps<T> for intrinsic elements (div, span, etc.)
// - Returns never for non-component types (should not occur in practice)
type ExtractComponentProps<T> =
  T extends React.FC<infer P>
    ? P
    : T extends React.ComponentType<infer P>
      ? P
      : T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<any> // eslint-disable-line @typescript-eslint/no-explicit-any
        ? ComponentProps<T>
        : never;

/**
 * Helper function to create lazy-loaded React components from named exports with automatic type inference.
 * Uses @loadable/component instead of React.lazy for better SSR support and loading states.
 * Reduces boilerplate when using loadable with named exports.
 *
 * @param importFn - Function that returns a promise resolving to a module
 * @param exportName - Name of the component export in the module
 * @returns A loadable React component with automatically inferred props type
 *
 * @example
 * // TypeScript automatically infers the component's props type
 * const MyComponent = createLazyComponentFromNamedExport(
 *   () => import('./MyComponent'),
 *   'MyComponent'
 * );
 */
export const createLazyComponentFromNamedExport = <
  // Modules can export values of any type, so Record<string, any> is necessary here
  TModule extends Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  TExportName extends keyof TModule & string,
>(
  importFn: () => Promise<TModule>,
  exportName: TExportName,
): LoadableComponent<ExtractComponentProps<TModule[TExportName]>> =>
  // Use resolveComponent instead of wrapping in { default: ... } to avoid
  // hoist-non-react-statics race condition with plain object wrappers.
  // See: https://github.com/gregberge/loadable-components/issues/420
  loadable(importFn, {
    resolveComponent: module => module[exportName],
  }) as LoadableComponent<ExtractComponentProps<TModule[TExportName]>>;
